'use client';

import * as React from 'react';
import { useEffect } from 'react';
import { useForm, Controller, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@unreserved/ui/components/input';
import { Label } from '@unreserved/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@unreserved/ui/components/select';
import { Button } from '@unreserved/ui/components/button';
import { AdminDialogWrapper } from './admin-dialog-wrapper';
import { Trash2 } from 'lucide-react';
import { addCoVendorSchema } from './schemas/add-co-vendor-schema';

const COUNTRY_CODES = [
  '+61',
  '+1',
  '+44',
  '+91',
  '+81',
  '+86',
  '+49',
  '+33',
] as const;

export interface CoVendorItem {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  countryCode?: string | null;
}

export interface AddCoVendorFormValues {
  name: string;
  email: string;
  countryCode: string;
  phone: string;
}

export interface AddCoVendorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingCoVendors?: CoVendorItem[];
  onRemoveCoVendor?: (id: string) => void;
  onConfirm: (values: AddCoVendorFormValues) => void;
  title?: string;
}

export function AddCoVendorDialog({
  open,
  onOpenChange,
  existingCoVendors = [],
  onRemoveCoVendor,
  onConfirm,
  title = 'Add Co-Vendor',
}: AddCoVendorDialogProps) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddCoVendorFormValues>({
    resolver: zodResolver(addCoVendorSchema) as Resolver<AddCoVendorFormValues>,
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      countryCode: '+61',
      phone: '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: '',
        email: '',
        countryCode: '+61',
        phone: '',
      });
    }
  }, [open, reset]);

  const handleCancel = () => {
    onOpenChange(false);
    reset();
  };

  const onSubmit = (data: AddCoVendorFormValues) => {
    onConfirm({
      name: data.name,
      email: data.email || '',
      countryCode: data.countryCode,
      phone: data.phone,
    });
    onOpenChange(false);
    reset();
  };

  const handleConfirm = () => {
    handleSubmit(onSubmit)();
  };

  return (
    <AdminDialogWrapper
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      contentPadding={false}
      footer={{
        onCancel: handleCancel,
        onConfirm: handleConfirm,
        confirmLabel: 'Save & Confirm',
      }}
    >
      <div className="space-y-4">
        {/* Existing co-vendors */}
        {existingCoVendors?.length > 0 && (
          <div className="space-y-2">
            {existingCoVendors?.map((cov) => (
              <div
                key={cov?.id}
                className="flex items-center justify-between gap-2 h-10 px-3 rounded-md border border-border-200 bg-white"
              >
                <span className="text-sm text-typography-900 truncate">
                  {cov?.name}
                </span>
                {onRemoveCoVendor && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemoveCoVendor(cov?.id)}
                    className="h-8 w-8 shrink-0 cursor-pointer text-typography-500 hover:text-error-600 hover:!bg-transparent"
                    aria-label={`Remove ${cov?.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Form fields */}
        <div className="space-y-4">
          <div>
            <Label
              htmlFor="co-vendor-name"
              className="text-sm font-normal text-typography-900"
            >
              Vendor Name <span className="text-error-600">*</span>
            </Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <>
                  <Input
                    id="co-vendor-name"
                    {...field}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                      field.onChange(value);
                    }}
                    placeholder="Enter here"
                    className={`mt-1 h-10 border-border-200 !bg-white ${
                      errors.name ? 'border-error-600' : ''
                    }`}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-error-600">
                      {errors.name.message}
                    </p>
                  )}
                </>
              )}
            />
          </div>
          <div>
            <Label
              htmlFor="co-vendor-email"
              className="text-sm font-normal text-typography-900"
            >
              Email Address
            </Label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <>
                  <Input
                    id="co-vendor-email"
                    type="email"
                    {...field}
                    value={field.value || ''}
                    placeholder="e.g. abc@gmail.com"
                    className={`mt-1 h-10 border-border-200 !bg-white ${
                      errors.email ? 'border-error-600' : ''
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-error-600">
                      {errors.email.message}
                    </p>
                  )}
                </>
              )}
            />
          </div>
          <div>
            <Label className="text-sm font-normal text-typography-900">
              Contact Number <span className="text-error-600">*</span>
            </Label>
            <div className="flex gap-2 mt-1">
              <Controller
                name="countryCode"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                  >
                    <SelectTrigger className="h-10 w-20 !bg-white border-border-200 shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="!bg-white">
                      {COUNTRY_CODES?.map((code) => (
                        <SelectItem
                          key={code}
                          value={code}
                          className="!bg-white hover:!bg-background-50 !text-typography-900"
                        >
                          {code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              <div className="flex-1">
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Input
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          field.onChange(value);
                        }}
                        placeholder="10 digits no."
                        className={`h-10 border-border-200 !bg-white ${
                          errors.phone ? 'border-error-600' : ''
                        }`}
                      />
                      {errors.phone && (
                        <p className="mt-1 text-sm text-error-600">
                          {errors.phone.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminDialogWrapper>
  );
}
